import type { SessionProfile } from "../../shared/types/domain";
import { demoOrganizationRepository } from "../organization/demoOrganizationRepository";
import { demoEnergyLedgerAdapter } from "../incentives/demoIncentivesRepository";
import { demoAuditAdapter, demoNotificationAdapter } from "../governance/demoGovernanceRepository";
import type { ShopState } from "./shop.types";
const KEY = "boom-v4-shop";
const seed: ShopState = {
  products: [
    product(
      "60000000-0000-4000-8000-000000000001",
      "专业吸汗手胶",
      "耐磨防滑，训练日常必备",
      "🎾",
      180,
      36,
    ),
    product(
      "60000000-0000-4000-8000-000000000002",
      "网爆星球运动毛巾",
      "轻量速干训练毛巾",
      "🏅",
      260,
      18,
    ),
    product(
      "60000000-0000-4000-8000-000000000003",
      "一小时场地券",
      "非高峰时段免费使用",
      "🎟️",
      500,
      12,
    ),
  ],
  orders: [],
};
function product(
  id: string,
  name: string,
  description: string,
  imageEmoji: string,
  points: number,
  stock: number,
) {
  return {
    id,
    name,
    description,
    imageEmoji,
    points,
    stock,
    scope: "company" as const,
    storeId: null,
    active: true,
    createdBy: "10000000-0000-4000-8000-000000000004",
    createdAt: "2026-07-01T00:00:00.000Z",
  };
}
const clone = <T>(x: T): T => structuredClone(x);
function load() {
  const raw = localStorage.getItem(KEY);
  if (!raw) return clone(seed);
  try {
    return JSON.parse(raw) as ShopState;
  } catch {
    return clone(seed);
  }
}
function save(s: ShopState) {
  localStorage.setItem(KEY, JSON.stringify(s));
}
function fail(m: string): never {
  throw new Error(m);
}
async function context(a: SessionProfile) {
  const org = await demoOrganizationRepository.getState();
  if (!org.accounts.some((x) => x.id === a.id && x.status === "active"))
    fail("当前账号不可用。");
  return org;
}
export const demoShopRepository = {
  async getState() {
    return load();
  },
  async reset() {
    localStorage.removeItem(KEY);
    return clone(seed);
  },
  async createProduct(
    actor: SessionProfile,
    input: {
      name: string;
      description: string;
      points: number;
      stock: number;
      imageEmoji: string;
    },
  ) {
    await context(actor);
    if (actor.role !== "super") fail("只有超管可以创建商城商品。");
    if (
      !input.name.trim() ||
      !input.description.trim() ||
      !Number.isInteger(input.points) ||
      input.points <= 0 ||
      !Number.isInteger(input.stock) ||
      input.stock < 0
    )
      fail("商品名称、说明、星能价格和库存无效。");
    const s = load(),
      next = {
        id: crypto.randomUUID(),
        name: input.name.trim(),
        description: input.description.trim(),
        imageEmoji: input.imageEmoji.trim() || "🎁",
        points: input.points,
        stock: input.stock,
        scope: "company" as const,
        storeId: null,
        active: true,
        createdBy: actor.id,
        createdAt: new Date().toISOString(),
      };
    s.products.unshift(next);
    save(s);
    demoAuditAdapter.record({ actorId: actor.id, action: "inventory.product.create", entityType: "shop_product", entityId: next.id, reason: `初始库存${next.stock}` });
    return clone(next);
  },
  async redeem(
    actor: SessionProfile,
    input: { productId: string; receiverName: string; receiverPhone: string },
  ) {
    await context(actor);
    if (actor.role !== "employee" || !actor.storeId)
      fail("只有在职员工可以兑换。");
    if (!input.receiverName.trim() || !/^1\d{10}$/.test(input.receiverPhone))
      fail("领取人和手机号无效。");
    const s = load(),
      p =
        s.products.find(
          (x) =>
            x.id === input.productId &&
            x.active &&
            (x.scope === "company" || x.storeId === actor.storeId),
        ) ?? fail("商品不可用。");
    if (p.stock <= 0) fail("商品库存不足。");
    if (demoEnergyLedgerAdapter.balanceOf(actor.id) < p.points)
      fail("星能余额不足。");
    const id = crypto.randomUUID(),
      now = new Date().toISOString(),
      next = {
        id,
        orderNumber: `EX-${Date.now()}`,
        employeeId: actor.id,
        storeId: actor.storeId,
        productId: p.id,
        productName: p.name,
        points: p.points,
        status: "pending_fulfillment" as const,
        receiverName: input.receiverName.trim(),
        receiverPhone: input.receiverPhone,
        fulfilledBy: null,
        createdAt: now,
        fulfilledAt: null,
      };
    p.stock -= 1;
    demoEnergyLedgerAdapter.spend(
      actor.id,
      actor.storeId,
      p.points,
      id,
      `兑换${p.name}`,
    );
    s.orders.unshift(next);
    save(s);
    demoAuditAdapter.record({ actorId: actor.id, action: "inventory.redeem", entityType: "redemption_order", entityId: next.id, reason: `库存-1；星能-${p.points}` });
    return clone(next);
  },
  async fulfill(actor: SessionProfile, orderId: string) {
    await context(actor);
    if (!["manager", "super"].includes(actor.role))
      fail("只有店长或超管可以确认发放。");
    const s = load(),
      o = s.orders.find((x) => x.id === orderId) ?? fail("兑换订单不存在。");
    if (o.status !== "pending_fulfillment") fail("兑换订单已经发放。");
    if (actor.role === "manager" && actor.storeId !== o.storeId)
      fail("不能处理其他门店兑换。");
    o.status = "fulfilled";
    o.fulfilledBy = actor.id;
    o.fulfilledAt = new Date().toISOString();
    save(s);
    demoAuditAdapter.record({ actorId: actor.id, action: "inventory.fulfill", entityType: "redemption_order", entityId: o.id, reason: "确认发放" });
    demoNotificationAdapter.notify({
      recipientId: o.employeeId,
      storeId: o.storeId,
      type: "shop",
      title: "兑换商品已发放",
      content: `${o.productName} 已确认发放。`,
      entityType: "redemption_order",
      entityId: o.id,
      dedupeKey: `redemption_order:${o.id}:shop`,
    });
    return clone(o);
  },
};
